import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../lib/auth-context';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  requireOrganization?: boolean;
}

export function ProtectedRoute({ children, requireOrganization = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, currentOrganization } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireOrganization && !currentOrganization) {
    return <Navigate to="/onboarding" replace />;
  }

  // 2FA enforcement: if the org requires 2FA and the user hasn't set it up,
  // redirect them to their profile page (where TwoFactorSection lives).
  // Allow access to /profile so they can actually set up 2FA.
  if (
    requireOrganization &&
    currentOrganization?.enforce2fa &&
    user &&
    !user.has2FAEnabled &&
    location.pathname !== '/profile'
  ) {
    return <Navigate to="/profile" state={{ enforce2fa: true }} replace />;
  }

  return <>{children}</>;
}
